import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navigation from './components/Navigation';
import Login from './components/Login';
import Signup from './components/Signup';
import VerifyEmail from './components/VerifyEmail';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import Dashboard from './components/Dashboard';
import QuickMeal from './components/QuickMeal';
import Settings from './components/Settings';
import Pantry from './components/Pantry';
import MyRecipes from './components/MyRecipes';
import ShoppingList from './components/ShoppingList';
import HouseholdAdmin from './components/HouseholdAdmin';
import UserGuide from './components/UserGuide';
import IconSprite from './components/IconSprite';
import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const AppShell: React.FC = () => {
  const { loading, username } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!username) {
    return (
      <Routes>
        <Route path="/signup" element={<Signup />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  return (
    <div className="app">
      <Navigation />
      <main className="main-content">
        <EmailVerificationBanner />
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/hungry" element={<QuickMeal />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/guide" element={<UserGuide />} />
          <Route path="/pantry" element={<Pantry />} />
          <Route path="/my-recipes" element={<MyRecipes />} />
          <Route path="/shopping" element={<ShoppingList />} />
          <Route path="/household-admin" element={<HouseholdAdmin />} />
          {/* Mailed link may be opened in an already-logged-in session. */}
          <Route path="/verify-email" element={<VerifyEmail />} />
        </Routes>
      </main>
    </div>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <IconSprite />
      <AuthProvider>
        <Router>
          <AppShell />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
